import React, { useContext, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import { ThemeContext } from './contexts/ThemeContext';
import CssBaseline from '@mui/material/CssBaseline';
import { ConfigProvider as AntdConfigProvider, theme as antdTheme } from 'antd';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import buildTheme from './theme';

// Contextos
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ActiveChatsProvider } from './contexts/ActiveChatsContext';

// Componentes de Layout
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import AppLayout from './components/Layout/AppLayout';
import FloatingChatButton from './components/FloatingChatButton';

// Componentes existentes
import IntegratedWhatsAppChat from './components/IntegratedWhatsAppChat';

// Páginas do WhatsApp existentes
import WhatsAppDashboard from './pages/whatsapp/Dashboard';
import WhatsAppTickets from './pages/whatsapp/Tickets/index.js';
import WhatsAppContacts from './pages/whatsapp/Contacts';
import WhatsAppUsers from './pages/whatsapp/Users';
import WhatsAppQueues from './pages/whatsapp/Queues';
import WhatsAppOperations from './pages/whatsapp/Operations';
import ReportsPage from './pages/whatsapp/Reports';
import WhatsAppSettings from './pages/whatsapp/Settings';
import CampaignsPage from './pages/whatsapp/Campaigns';
import SmsGatewaysPage from './pages/whatsapp/SmsGateways';
import AutomationWorkflowsPage from './pages/whatsapp/Automation/Workflows';
import AutomationBuilderPage from './pages/whatsapp/Automation/Builder';
import WhatsAppHelp from './pages/whatsapp/Help';
import WhatsAppTicketsCustom from './pages/whatsapp/TicketsCustom';
import WhatsAppWhatsAppConnections from './pages/whatsapp/WhatsAppConnections';
import ExternalApiIntegrationsPage from './pages/whatsapp/ExternalApiIntegrations';
import ExternalMessageTemplatesPage from './pages/whatsapp/ExternalMessageTemplates';
import ExternalApiSendsPage from './pages/whatsapp/ExternalApiSends';
import ExternalApiWebhooksPage from './pages/whatsapp/ExternalApiWebhooks';
import ExternalApiQueuePage from './pages/whatsapp/ExternalApiQueue';
import OmniDashboardPage from './pages/whatsapp/OmniDashboard';
import PauseTypesPage from './pages/whatsapp/PauseTypes';
import SLAPoliciesPage from './pages/whatsapp/SLA';
import ProtheusPagamentosPage from './pages/protheus/Pagamentos';
import ContentPage from './pages/content';
import VideosPage from './pages/videos';
import TemplatesPage from './pages/templates';
import YouTubePage from './pages/youtube';
import YouTubeOAuthCallback from './pages/youtube/OAuthCallback';
import YouTubeScriptsPage from './pages/youtube-scripts';
import VoiceAiPage from './pages/voice-ai';
import ImageAiPage from './pages/image-ai';
import AiSettingsPage from './pages/ai-settings';
import VideoEditorPage from './pages/video-editor';
import YouTubeAnalyticsPage from './pages/youtube-analytics';
import YouTubeAutomationPage from './pages/youtube-automation';
import ProductionListPage from './pages/production';
import ProductionDetailPage from './pages/production/TrackingPage';
import ProductionReviewPage from './pages/production/ProductionReviewPage';
import ProductionDashboardPage from './pages/production-dashboard';
import DesignSystemPage from './pages/design-system';

// Páginas de autenticação
import LoginPage from './components/LoginPage';

import './cripto-gt/index.css';

const CriptoDashboardPremiumPage = React.lazy(() => import('./cripto-gt/pages/DashboardPremium'));
const CriptoContasPage = React.lazy(() => import('./cripto-gt/pages/Contas'));
const CriptoOrdensAutoPage = React.lazy(() => import('./cripto-gt/pages/OrdensAuto'));
const CriptoMonitoramentoSaldosPage = React.lazy(() => import('./cripto-gt/pages/MonitoramentoSaldos'));
const CriptoMonitoramentoMoedasPage = React.lazy(() => import('./cripto-gt/pages/MonitoramentoMoedas'));
const CriptoMonitoramentoAlertasPage = React.lazy(() => import('./cripto-gt/pages/MonitoramentoAlertas'));
const CriptoAnaliseIAPage = React.lazy(() => import('./cripto-gt/pages/AnaliseIA'));
const CriptoSugestoesIAPage = React.lazy(() => import('./cripto-gt/pages/SugestoesIA'));
const CriptoPerformanceIAPage = React.lazy(() => import('./cripto-gt/pages/PerformanceIA'));
const CriptoAuditoriaIAPage = React.lazy(() => import('./cripto-gt/pages/AuditoriaIA'));
const CriptoRelatorioIAPage = React.lazy(() => import('./cripto-gt/pages/RelatorioIA'));
const CriptoRelatorioGanhosPerdasPage = React.lazy(() => import('./cripto-gt/pages/RelatorioGanhosPerdas'));
const CriptoRelatorioFluxoCaixaPage = React.lazy(() => import('./cripto-gt/pages/RelatorioFluxoCaixa'));
const CriptoRelatorioFiscalPage = React.lazy(() => import('./cripto-gt/pages/RelatorioFiscal'));
const CriptoGestaoFinanceiraPage = React.lazy(() => import('./cripto-gt/pages/GestaoFinanceira'));
const CriptoGestaoFinanceiraCompletaPage = React.lazy(() => import('./cripto-gt/pages/GestaoFinanceiraCompleta'));
const CriptoRendaPassivaPage = React.lazy(() => import('./cripto-gt/pages/RendaPassiva'));
const CriptoAssinaturaPage = React.lazy(() => import('./cripto-gt/pages/Assinatura'));
const CriptoAutoModePage = React.lazy(() => import('./cripto-gt/pages/AutoMode'));
const CriptoStrategiesPage = React.lazy(() => import('./cripto-gt/pages/Strategies'));
const CriptoAlertsCenterPage = React.lazy(() => import('./cripto-gt/pages/AlertsCenter'));
const CriptoValidationLabPage = React.lazy(() => import('./cripto-gt/pages/ValidationLab'));
const CriptoConfiguracoesPage = React.lazy(() => import('./cripto-gt/pages/Configuracoes'));
const CriptoConfiguracaoIAPage = React.lazy(() => import('./cripto-gt/pages/ConfiguracaoIA'));
const CriptoMoedasPrincipaisPage = React.lazy(() => import('./cripto-gt/pages/MoedasPrincipais'));
const CriptoGestaoUsuariosPage = React.lazy(() => import('./cripto-gt/pages/GestaoUsuarios'));
const CriptoAdminFeedbacksPage = React.lazy(() => import('./cripto-gt/pages/AdminFeedbacks'));

const ThemedProviders = ({ children }) => {
  const { theme } = useContext(ThemeContext) || { theme: 'dark' };
  const muiTheme = useMemo(() => {
    try {
      const next = buildTheme(theme === 'dark' ? 'dark' : 'light');
      if (!next || !next.palette || !next.palette.primary) return createTheme();
      return next;
    } catch (_) {
      return createTheme();
    }
  }, [theme]);
  return (
    <MUIThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MUIThemeProvider>
  );
};

const CriptoLayout = () => {
  const { theme } = useContext(ThemeContext) || { theme: 'dark' };
  const [simpleMode, setSimpleMode] = React.useState(true);
  const antdProviderTheme = useMemo(() => {
    const isDark = theme === 'dark';
    return {
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: 'var(--v-primary)',
        colorInfo: 'var(--v-primary)',
        colorSuccess: 'var(--v-success)',
        colorWarning: 'var(--v-warning)',
        colorError: 'var(--v-error)',
        colorTextBase: 'var(--v-fg)',
        colorText: 'var(--v-fg)',
        colorTextSecondary: 'var(--v-fg-muted)',
        colorBorder: 'var(--v-border)',
        borderRadius: 10,
        borderRadiusLG: 14,
        colorBgBase: isDark ? 'var(--v-bg-1)' : 'var(--cor-fundo)',
        colorBgContainer: 'transparent',
        colorBgElevated: 'var(--v-surface-1)'
      }
    };
  }, [theme]);
  return (
    <AntdConfigProvider theme={antdProviderTheme}>
      <div className="cripto-module">
        <Outlet context={{ simpleMode, setSimpleMode }} />
      </div>
    </AntdConfigProvider>
  );
};

function App() {
  return (
    <ThemedProviders>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AuthProvider>
          <NotificationProvider>
            <ActiveChatsProvider>
                <div className="App">
                  <Routes>
                    {/* Rota de Login */}
                    <Route 
                      path="/login" 
                      element={
                        <PublicRoute>
                          <LoginPage />
                        </PublicRoute>
                      } 
                    />

                    {/* Rotas com AppLayout */}
                    <Route 
                      path="/" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <Navigate to="/dashboard" replace />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route path="/consolidated-dashboard" element={<Navigate to="/dashboard" replace />} />
                    
                    {/* Rotas do WhatsApp com AppLayout */}
                    <Route 
                      path="/dashboard" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppDashboard />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/tickets" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppTickets />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/chat" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <Navigate to="/integrated-whatsapp-chat" replace />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/contacts" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppContacts />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/connections" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppWhatsAppConnections />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/users" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppUsers />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/queues" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppQueues />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/operations" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppOperations />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route
                      path="/sla"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <SLAPoliciesPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route 
                      path="/reports" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ReportsPage />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/quick-answers" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <Navigate to="/settings" replace />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/settings" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppSettings />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route
                      path="/external-api-integrations"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ExternalApiIntegrationsPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/external-message-templates"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ExternalMessageTemplatesPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/external-api-sends"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ExternalApiSendsPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/external-api-webhooks"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ExternalApiWebhooksPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/external-api-queue"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ExternalApiQueuePage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/omni-dashboard"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <OmniDashboardPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/pause-types"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <PauseTypesPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route 
                      path="/campaigns" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <CampaignsPage />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route
                      path="/sms-gateways"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <SmsGatewaysPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/automation"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <AutomationWorkflowsPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/automation/:id"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <AutomationBuilderPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route 
                      path="/help" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppHelp />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/tickets-custom" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppTicketsCustom />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/whatsapp-chats" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <Navigate to="/integrated-whatsapp-chat" replace />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/whatsapp-connections" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <WhatsAppWhatsAppConnections />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/integrated-whatsapp-chat" 
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <IntegratedWhatsAppChat />
                          </AppLayout>
                        </PrivateRoute>
                      } 
                    />

                    <Route
                      path="/protheus/pagamentos"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ProtheusPagamentosPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/content"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ContentPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/videos"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <VideosPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/templates"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <TemplatesPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/youtube"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <YouTubePage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/youtube-analytics"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <YouTubeAnalyticsPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/youtube-automation"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <YouTubeAutomationPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/production"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ProductionListPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/production-dashboard"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ProductionDashboardPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/design-system"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <DesignSystemPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/production/:id"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ProductionDetailPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/production/:id/review"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ProductionReviewPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/youtube/oauth-callback"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <YouTubeOAuthCallback />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/youtube-scripts"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <YouTubeScriptsPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/ai-settings"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <AiSettingsPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/voz-ia"
                      element={<Navigate to="/voice-ai" replace />}
                    />
                    <Route
                      path="/voice-ai"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <VoiceAiPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/image-ai"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <ImageAiPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/video-editor"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <Navigate to="/videos" replace />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/video-editor/:id"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <VideoEditorPage />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/cripto"
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <React.Suspense fallback={null}>
                              <CriptoLayout />
                            </React.Suspense>
                          </AppLayout>
                        </PrivateRoute>
                      }
                    >
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<React.Suspense fallback={null}><CriptoDashboardPremiumPage /></React.Suspense>} />
                      <Route path="contas" element={<React.Suspense fallback={null}><CriptoContasPage /></React.Suspense>} />
                      <Route path="ordens" element={<React.Suspense fallback={null}><CriptoOrdensAutoPage /></React.Suspense>} />
                      <Route path="monitoramento-saldos" element={<React.Suspense fallback={null}><CriptoMonitoramentoSaldosPage /></React.Suspense>} />
                      <Route path="monitoramento-moedas" element={<React.Suspense fallback={null}><CriptoMonitoramentoMoedasPage /></React.Suspense>} />
                      <Route path="monitoramento-alertas" element={<React.Suspense fallback={null}><CriptoMonitoramentoAlertasPage /></React.Suspense>} />
                      <Route path="analise-ia" element={<React.Suspense fallback={null}><CriptoAnaliseIAPage /></React.Suspense>} />
                      <Route path="sugestoes-ia" element={<React.Suspense fallback={null}><CriptoSugestoesIAPage /></React.Suspense>} />
                      <Route path="performance-ia" element={<React.Suspense fallback={null}><CriptoPerformanceIAPage /></React.Suspense>} />
                      <Route path="auditoria-ia" element={<React.Suspense fallback={null}><CriptoAuditoriaIAPage /></React.Suspense>} />
                      <Route path="relatorio-ia" element={<React.Suspense fallback={null}><CriptoRelatorioIAPage /></React.Suspense>} />
                      <Route path="relatorio-ganhos-perdas" element={<React.Suspense fallback={null}><CriptoRelatorioGanhosPerdasPage /></React.Suspense>} />
                      <Route path="relatorio-fluxo-caixa" element={<React.Suspense fallback={null}><CriptoRelatorioFluxoCaixaPage /></React.Suspense>} />
                      <Route path="relatorio-fiscal" element={<React.Suspense fallback={null}><CriptoRelatorioFiscalPage /></React.Suspense>} />
                      <Route path="gestao-financeira" element={<React.Suspense fallback={null}><CriptoGestaoFinanceiraPage /></React.Suspense>} />
                      <Route path="financas-pessoais" element={<React.Suspense fallback={null}><CriptoGestaoFinanceiraCompletaPage /></React.Suspense>} />
                      <Route path="renda-passiva" element={<React.Suspense fallback={null}><CriptoRendaPassivaPage /></React.Suspense>} />
                      <Route path="assinatura" element={<React.Suspense fallback={null}><CriptoAssinaturaPage /></React.Suspense>} />
                      <Route path="auto-mode" element={<React.Suspense fallback={null}><CriptoAutoModePage /></React.Suspense>} />
                      <Route path="estrategias" element={<React.Suspense fallback={null}><CriptoStrategiesPage /></React.Suspense>} />
                      <Route path="alertas" element={<React.Suspense fallback={null}><CriptoAlertsCenterPage /></React.Suspense>} />
                      <Route path="validacao" element={<React.Suspense fallback={null}><CriptoValidationLabPage /></React.Suspense>} />
                      <Route path="configuracoes" element={<React.Suspense fallback={null}><CriptoConfiguracoesPage /></React.Suspense>} />
                      <Route path="configuracoes/ia" element={<React.Suspense fallback={null}><CriptoConfiguracaoIAPage /></React.Suspense>} />
                      <Route path="configuracoes/moedas-principais" element={<React.Suspense fallback={null}><CriptoMoedasPrincipaisPage /></React.Suspense>} />
                      <Route path="admin/users" element={<React.Suspense fallback={null}><CriptoGestaoUsuariosPage /></React.Suspense>} />
                      <Route path="admin/feedbacks" element={<React.Suspense fallback={null}><CriptoAdminFeedbacksPage /></React.Suspense>} />
                      <Route path="*" element={<Navigate to="dashboard" replace />} />
                    </Route>

                    {/* Fallback para rotas não encontradas */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>

                  <FloatingChatButton />
                  <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                  />
                </div>
            </ActiveChatsProvider>
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </ThemedProviders>
  );
}

export default App;
